'use client';
import { useState, useEffect, useCallback, useRef } from 'react';

import type {
  NetworkStatus,
  NetworkStatusOptions,
  NetworkStatusHook,
  ConnectionQuality,
  NetworkInformation,
  NetworkConnectionType,
  NetworkEffectiveType,
} from '../types/network';

/**
 * Get the Network Information API connection object
 * Handles vendor prefixes and feature detection
 */
function getNetworkConnection(): NetworkInformation | undefined {
  if (typeof navigator === 'undefined') return undefined;

  return navigator.connection || navigator.mozConnection || navigator.webkitConnection;
}

/**
 * Calculate connection quality based on effective type and other metrics
 */
function calculateConnectionQuality(status: NetworkStatus): ConnectionQuality {
  if (!status.isOnline) return 'offline';

  const { effectiveType, downlink, rtt } = status;

  // Use effectiveType as primary indicator
  if (effectiveType === '4g') {
    // Further refine based on actual metrics if available
    if (downlink && rtt) {
      if (downlink >= 10 && rtt <= 50) return 'excellent';
      if (downlink >= 5 && rtt <= 100) return 'good';
      return 'fair';
    }
    return 'good';
  }

  if (effectiveType === '3g') return 'fair';
  if (effectiveType === '2g' || effectiveType === 'slow-2g') return 'poor';

  // Fallback based on downlink speed alone
  if (downlink) {
    if (downlink >= 10) return 'excellent';
    if (downlink >= 5) return 'good';
    if (downlink >= 1) return 'fair';
    return 'poor';
  }

  // Default for unknown but online
  return 'fair';
}

/**
 * Get current network status from browser APIs
 */
function getCurrentNetworkStatus(): NetworkStatus {
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  const connection = getNetworkConnection();

  return {
    isOnline,
    connectionType: (connection?.type as NetworkConnectionType) || 'unknown',
    effectiveType: (connection?.effectiveType as NetworkEffectiveType) || 'unknown',
    downlink: connection?.downlink,
    rtt: connection?.rtt,
    saveData: connection?.saveData,
    timestamp: Date.now(),
  };
}

/**
 * Debounce function to prevent rapid updates
 */
function debounce<T extends (...args: never[]) => void>(func: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout>;

  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

/**
 * Hook for monitoring network status and connection quality
 * Provides real-time network connectivity information for PWA functionality
 *
 * @param options - Configuration options for the hook
 * @returns Network status information and utilities
 *
 * @example
 * ```tsx
 * const { isOnline, connectionQuality, networkStatus } = useNetworkStatus({
 *   debounceMs: 500,
 *   onStatusChange: (status) => logger.info('Network changed', { status })
 * });
 *
 * if (!isOnline) {
 *   return <OfflineMessage />;
 * }
 *
 * if (connectionQuality === 'poor') {
 *   return <SlowConnectionWarning />;
 * }
 * ```
 */
export function useNetworkStatus(options: NetworkStatusOptions = {}): NetworkStatusHook {
  const {
    debounceMs = 500,
    onStatusChange,
    enableSpeedTest = false,
    speedTestInterval = 30000,
  } = options;

  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(getCurrentNetworkStatus);
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality>(
    calculateConnectionQuality(getCurrentNetworkStatus())
  );

  const listenersRef = useRef<Set<(status: NetworkStatus) => void>>(new Set());
  const speedTestIntervalRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const debouncedUpdateRef = useRef<(() => void) | undefined>(undefined);

  // Store callbacks in refs to prevent dependency issues
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  // Store network status in ref to avoid dependency issues in callbacks
  const networkStatusRef = useRef(networkStatus);
  useEffect(() => {
    networkStatusRef.current = networkStatus;
  }, [networkStatus]);

  // Update network status and notify listeners - no external dependencies
  const updateStatus = useCallback((newStatus?: NetworkStatus) => {
    const status = newStatus || getCurrentNetworkStatus();
    setNetworkStatus(status);
    setConnectionQuality(calculateConnectionQuality(status));

    // Notify external callback (using ref to avoid dependency)
    onStatusChangeRef.current?.(status);

    // Notify all subscribers
    listenersRef.current.forEach((listener) => listener(status));
  }, []);

  // Create debounced update function - only depends on debounceMs primitive
  useEffect(() => {
    debouncedUpdateRef.current = debounce(updateStatus, debounceMs);
  }, [updateStatus, debounceMs]);

  // Subscribe to network status changes
  const subscribe = useCallback((callback: (status: NetworkStatus) => void) => {
    listenersRef.current.add(callback);

    // Return unsubscribe function
    return () => {
      listenersRef.current.delete(callback);
    };
  }, []);

  // Manually check network status
  const checkStatus = useCallback(() => {
    updateStatus();
  }, [updateStatus]);

  // Run speed test (if enabled) - uses ref to avoid dependency on networkStatus
  const runSpeedTest = useCallback(async (): Promise<number> => {
    if (!networkStatusRef.current.isOnline) {
      throw new Error('Cannot run speed test while offline');
    }

    try {
      // Simple speed test using a small image download
      const testUrl = 'https://www.google.com/images/phd/px.gif';
      const startTime = performance.now();

      const response = await fetch(testUrl, {
        method: 'GET',
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error('Speed test failed');
      }

      const endTime = performance.now();
      const duration = endTime - startTime;

      // Estimate speed based on response time
      // This is a simple approximation
      const estimatedSpeed = 1000 / duration; // Mbps approximation

      return Math.max(0.1, Math.min(100, estimatedSpeed));
    } catch {
      // Speed test failed - return 0 (errors are expected during offline/poor connection)
      return 0;
    }
  }, []);

  // Store updateStatus in ref to use in effect without dependency
  const updateStatusRef = useRef(updateStatus);
  useEffect(() => {
    updateStatusRef.current = updateStatus;
  }, [updateStatus]);

  // Store runSpeedTest in ref to use in effect without dependency
  const runSpeedTestRef = useRef(runSpeedTest);
  useEffect(() => {
    runSpeedTestRef.current = runSpeedTest;
  }, [runSpeedTest]);

  // Setup event listeners - only run on mount and when primitive options change
  useEffect(() => {
    const handleOnline = () => {
      debouncedUpdateRef.current?.();
    };

    const handleOffline = () => {
      // Don't debounce offline events - report immediately
      updateStatusRef.current();
    };

    const handleConnectionChange = () => {
      debouncedUpdateRef.current?.();
    };

    // Add browser event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Add Network Information API listener if available
    const connection = getNetworkConnection();
    if (connection) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Setup speed test interval if enabled
    if (enableSpeedTest) {
      const runIntervalSpeedTest = async () => {
        try {
          // Check current online status using ref
          if (!networkStatusRef.current.isOnline) return;

          const speed = await runSpeedTestRef.current();
          // Update status with speed test result
          updateStatusRef.current({
            ...getCurrentNetworkStatus(),
            downlink: speed,
          });
        } catch {
          // Interval speed test failed - silently continue (expected during connectivity issues)
        }
      };

      speedTestIntervalRef.current = setInterval(runIntervalSpeedTest, speedTestInterval);
    }

    // Cleanup
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);

      if (connection) {
        connection.removeEventListener('change', handleConnectionChange);
      }

      if (speedTestIntervalRef.current) {
        clearInterval(speedTestIntervalRef.current);
      }
    };
  }, [enableSpeedTest, speedTestInterval]); // Only primitive dependencies

  return {
    isOnline: networkStatus.isOnline,
    networkStatus,
    connectionQuality,
    subscribe,
    checkStatus,
    ...(enableSpeedTest ? { runSpeedTest } : {}),
  };
}
